import { buildPaymentCallbackUrl } from '../recharge-shared'
import type { CashfreeOrderCreatePayload } from '../types'
import { buildUpgradePlanPatch } from '../upgrade-plan'

// The netbanking checkout, below the React boundary.
//
// Netbanking always runs on CASHFREE — Razorpay does not support it — so a
// Razorpay-backed flow still creates a Cashfree order here. That is the whole
// point of this module: the processor is not a caller's choice.
//
// Kept free of React, react-query, the billing service and the toast module, and
// every side effect is injected. Two reasons, and the second is the load-bearing
// one:
//
//  1. Returning a result instead of toasting keeps the decision testable.
//  2. Sibling billing test files replace `billing-service` and `v2/utils/toasts`
//     process-globally with bun's non-restoring mock.module (AGENTS.md failure
//     mode 1), so anything reaching those modules cannot be render-tested
//     reliably in the full suite — see the note in
//     use-razorpay-standard-checkout.test.tsx. Below this line there is nothing
//     for a leaked mock to poison.

export type CashfreeNetbankingFailure =
  | 'no-amount'
  | 'no-account'
  | 'no-session'
  | 'create-failed'
  | 'launch-failed'

export type CashfreeNetbankingResult =
  | { ok: true }
  | { ok: false; reason: CashfreeNetbankingFailure; error?: unknown }

// The order payload, in one place so the entry points cannot drift into slightly
// different ones.
//
// `isTrial` carries the plan purchase. Netbanking is a redirect rail — the credit
// lands on Cashfree's webhook long after this tab is gone — so `upgrade_plan` on the
// order is the ONLY mechanism that can upgrade a free-trial account paying this way.
// It is load-bearing here because INR now opens the Razorpay modal for every account
// (../recharge-surface), so the generic RechargeModal, which used to be the trial's
// netbanking sender, no longer serves INR at all.
export const buildCashfreeNetbankingOrder = (
  accountId: number,
  amount: number,
  isTrial = false,
): CashfreeOrderCreatePayload => ({
  account_id: accountId,
  amount,
  product_type: 'plivo',
  payment_method_type: 'netbanking',
  return_url: buildPaymentCallbackUrl('netbanking'),
  ...buildUpgradePlanPatch(isTrial),
})

interface RunCashfreeNetbankingParams {
  accountId: number
  amount: number
  // Free-trial account: the order carries upgrade_plan so the capture webhook
  // upgrades the plan after crediting.
  isTrial?: boolean
  createOrder: (payload: CashfreeOrderCreatePayload) => Promise<{ payment_session_id?: string }>
  launchCheckout: (paymentSessionId: string) => Promise<void>
}

// Resolves `{ ok: true }` only when the redirect was handed off. In production
// that redirect tears the page down, so nothing after it runs — callers must not
// depend on work sequenced after a successful call.
export const runCashfreeNetbankingCheckout = async ({
  accountId,
  amount,
  isTrial = false,
  createOrder,
  launchCheckout,
}: RunCashfreeNetbankingParams): Promise<CashfreeNetbankingResult> => {
  if (!amount) return { ok: false, reason: 'no-amount' }
  // getAccountId() returns 0 on a cold profile cache; an order for account 0 is
  // charged to nobody.
  if (!accountId) return { ok: false, reason: 'no-account' }

  let order: { payment_session_id?: string }
  try {
    order = await createOrder(buildCashfreeNetbankingOrder(accountId, amount, isTrial))
  } catch (error) {
    return { ok: false, reason: 'create-failed', error }
  }

  // An order without a session is unusable — redirecting anyway lands the
  // customer on a blank Cashfree page with nothing to explain it.
  if (!order.payment_session_id) return { ok: false, reason: 'no-session' }

  try {
    await launchCheckout(order.payment_session_id)
  } catch (error) {
    // The SDK script can be blocked or fail to load. Say so, rather than leaving
    // a dialog that silently did nothing.
    return { ok: false, reason: 'launch-failed', error }
  }
  return { ok: true }
}
